import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Stethoscope,
  MapPin,
  Phone,
  Clock,
  BedDouble,
  Users,
  ClipboardList,
  Landmark,
  Banknote,
} from "lucide-react";
import SEO from "../components/SEO";

function capitalize(str) {
  if (!str) return "";
  return str.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
}

function formatCnpj(cnpj) {
  if (!cnpj || cnpj.length !== 14) return cnpj || null;
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

function formatPhone(tel) {
  if (!tel) return null;
  return tel;
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2.5">
        {icon && <span className="text-[var(--accent)]">{icon}</span>}
        <h2 className="text-base font-bold text-[var(--text)]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5 border-b border-[var(--border)] last:border-0">
      <div className="p-1 rounded-md bg-[var(--accent-soft)] text-[var(--accent)] mt-0.5">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-[var(--muted)] uppercase tracking-wider leading-none">{label}</p>
        <p className="text-sm font-medium text-[var(--text)] mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function NameChip({ children, title }) {
  return (
    <span title={title} className="text-xs px-2 py-1 rounded-md bg-[var(--background)] border border-[var(--border)] text-[var(--text)]">
      {children}
    </span>
  );
}

function RoleGroup({ especialidade, total, nomes }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)] mb-2">
        {especialidade} <span className="text-[var(--accent)]">· {total}</span>
      </p>
      {nomes?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {nomes.map((n) => <NameChip key={n}>{capitalize(n)}</NameChip>)}
        </div>
      )}
    </div>
  );
}

export default function MedFacilityPage() {
  const { cnes } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!cnes) return;
    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await axios.get(`/med/estabelecimentos/${cnes}`);
        if (!alive) return;
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          setError("Estabelecimento não encontrado");
        }
      } catch (err) {
        if (alive) {
          setError(err.response?.status === 404 ? "Estabelecimento não encontrado" : "Erro ao carregar dados");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [cnes]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="inline-block w-8 h-8 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-[var(--muted)]">Carregando estabelecimento...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-xl text-red-500 mb-4">{error || "Erro desconhecido"}</p>
        <button onClick={() => navigate(-1)} className="text-[var(--accent)] hover:underline flex items-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>
    );
  }

  const endereco = [
    data.logradouro,
    data.numero && data.numero !== "S/N" ? data.numero : null,
    data.complemento,
    data.bairro,
  ].filter(Boolean).join(", ");
  const cidadeUf = [capitalize(data.municipioNome), data.uf].filter(Boolean).join(" - ");
  const enderecoCompleto = [endereco, cidadeUf, data.cep].filter(Boolean).join(" - ");
  const mapQuery = [data.logradouro, data.numero, data.bairro, data.municipioNome, data.uf, data.cep]
    .filter(Boolean).join(", ");

  const seoDescription = `${capitalize(data.nomeFantasia)} — ${capitalize(data.tipoEstabelecimento)} em ${capitalize(data.municipioNome)}, ${data.uf}. Endereço, telefone, horário de funcionamento e serviços de saúde (CNES/DATASUS).`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: data.nomeFantasia,
    address: {
      "@type": "PostalAddress",
      streetAddress: endereco || undefined,
      addressLocality: data.municipioNome || undefined,
      addressRegion: data.uf || undefined,
      postalCode: data.cep || undefined,
      addressCountry: "BR",
    },
    telephone: data.telefone || undefined,
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <SEO
        title={`${capitalize(data.nomeFantasia)} - ${capitalize(data.municipioNome)}/${data.uf}`}
        description={seoDescription}
        path={`/med/${cnes}`}
        jsonLd={jsonLd}
      />

      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      {/* Header */}
      <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
            <Stethoscope size={20} className="text-sky-500" />
          </div>
          <div className="flex-1 min-w-0">
            {data.servicos?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1">
                {data.servicos.map((s) => (
                  <span
                    key={s.codigo}
                    className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[var(--accent-soft)] text-[var(--accent)]"
                  >
                    {capitalize(s.nome)}
                  </span>
                ))}
              </div>
            )}
            <h1 className="text-xl font-extrabold tracking-tight text-[var(--text)]">
              {capitalize(data.nomeEmpresarial || data.nomeFantasia)}
            </h1>
          </div>
        </div>
      </div>

      {/* Contato / Endereço — map goes beside the info, not stacked below it */}
      <Section title="Contato e Endereço" icon={<MapPin size={16} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div>
            <InfoRow icon={<MapPin size={14} />} label="Endereço" value={enderecoCompleto || null} />
            {formatPhone(data.telefone) && (
              <a
                href={`tel:${data.telefone.replace(/\D/g, "")}`}
                className="flex items-center gap-2 mt-2.5 px-3 py-2 rounded-lg bg-[var(--accent-soft)] text-[var(--accent)] font-bold text-sm w-fit hover:opacity-80 transition-opacity"
              >
                <Phone size={16} />
                {formatPhone(data.telefone)}
              </a>
            )}
          </div>
          {mapQuery && (
            <div className="rounded-lg overflow-hidden border border-[var(--border)]">
              <iframe
                title="Mapa do estabelecimento"
                width="100%"
                height="150"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
              />
            </div>
          )}
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Horário de funcionamento */}
        <Section title="Horário de Funcionamento" icon={<Clock size={16} />}>
          {data.horarioFuncionamento?.tipo === "sempre_aberto" && (
            <p className="text-sm font-medium text-[var(--text)]">Sempre aberto</p>
          )}
          {data.horarioFuncionamento?.tipo === "horario_semanal" && (
            <div className="space-y-1.5">
              {data.horarioFuncionamento.dias.map((d) => (
                <div key={d.dia} className="flex justify-between text-sm">
                  <span className="text-[var(--muted)]">{d.dia}</span>
                  <span className="text-[var(--text)] font-medium">{d.horario}</span>
                </div>
              ))}
            </div>
          )}
          {(!data.horarioFuncionamento || data.horarioFuncionamento.tipo === "nao_informado" || data.horarioFuncionamento.tipo === "desconhecido") && (
            <p className="text-sm text-[var(--muted)]">Não informado</p>
          )}
        </Section>

        {/* Identificação legal */}
        <Section title="Identificação" icon={<Landmark size={16} />}>
          <InfoRow icon={<Landmark size={14} />} label="CNPJ" value={formatCnpj(data.cnpj)} />
          <InfoRow icon={<Landmark size={14} />} label="Gestão" value={capitalize(data.gestao)} />
          <InfoRow icon={<Landmark size={14} />} label="Dependência" value={capitalize(data.dependencia)} />
          <InfoRow icon={<Landmark size={14} />} label="Cadastrado em" value={data.cadastradoEm} />
        </Section>
      </div>

      {(data.leitos?.length > 0 || data.habilitacoes?.length > 0 || data.incentivos?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Leitos */}
          {data.leitos?.length > 0 && (
            <Section title="Leitos" icon={<BedDouble size={16} />}>
              <div className="space-y-1.5">
                {data.leitos.map((l, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-[var(--muted)] truncate pr-2">{capitalize(l.descricao)}</span>
                    <span className="text-[var(--text)] font-medium whitespace-nowrap">
                      {l.existentes} {l.sus > 0 && <span className="text-emerald-500">({l.sus} SUS)</span>}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Habilitações */}
          {data.habilitacoes?.length > 0 && (
            <Section title="Habilitações" icon={<ClipboardList size={16} />}>
              <div className="space-y-2">
                {data.habilitacoes.map((h, i) => (
                  <div key={i} className="text-sm border-b border-[var(--border)] last:border-0 pb-2 last:pb-0">
                    <p className="font-medium text-[var(--text)]">{capitalize(h.descricao)}</p>
                    <p className="text-xs text-[var(--muted)]">Desde {h.competenciaInicial}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Incentivos */}
          {data.incentivos?.length > 0 && (
            <Section title="Incentivos" icon={<Banknote size={16} />}>
              <div className="space-y-2">
                {data.incentivos.map((inc, i) => (
                  <div key={i} className="text-sm border-b border-[var(--border)] last:border-0 pb-2 last:pb-0">
                    <p className="font-medium text-[var(--text)]">{capitalize(inc.descricao)}</p>
                    <p className="text-xs text-[var(--muted)]">Desde {inc.competenciaInicial}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* Profissionais — full width: name lists can get long, needs the room */}
      {data.totalProfissionais > 0 && (
        <Section title="Profissionais" icon={<Users size={16} />}>
          <p className="text-sm font-medium text-[var(--text)] mb-3">
            {data.totalProfissionais} profissional{data.totalProfissionais !== 1 ? "is" : ""} registrado{data.totalProfissionais !== 1 ? "s" : ""}
          </p>
          {data.profissionaisPorEspecialidade?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {data.profissionaisPorEspecialidade.map((p) => (
                <RoleGroup key={p.especialidade} {...p} />
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Equipes de saúde — full width, one card per team */}
      {data.equipes?.length > 0 && (
        <Section title="Equipes de Saúde" icon={<Users size={16} />}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {data.equipes.map((eq, i) => (
              <div key={i} className="rounded-lg border border-[var(--border)] p-3">
                <p className="font-medium text-sm text-[var(--text)]">{capitalize(eq.tipo)}</p>
                <p className="text-xs text-[var(--muted)] mt-0.5">
                  {capitalize(eq.nome)}
                  {eq.dataAtivacao && ` · desde ${eq.dataAtivacao}`}
                </p>
                {eq.membros?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {eq.membros.map((m, j) => (
                      <NameChip key={j} title={m.atividade ? capitalize(m.atividade) : undefined}>
                        {capitalize(m.nome)}
                      </NameChip>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
